<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { format, parseISO } from 'date-fns';
import contactsAPI from 'dashboard/api/contacts';

const props = defineProps({
  schedule: { type: Object, default: null },
  resources: { type: Array, default: () => [] },
  availability: { type: Array, default: () => [] },
  preselectedDate: { type: Date, default: null },
  preselectedSlot: { type: Object, default: null },
  editingBooking: { type: Object, default: null }, // existing booking to edit
  isSaving: { type: Boolean, default: false },
  isBlock: { type: Boolean, default: false }, // block mode (no attendee info)
  originOptions: { type: Array, default: () => ['WhatsApp', 'Instagram', 'Balcão'] },
});

const emit = defineEmits(['save', 'update', 'close']);

const isEditMode = computed(() => !!props.editingBooking);
const isBlockMode = computed(() => props.isBlock || props.editingBooking?.booking_type === 'block');

// ─── Contact search ──────────────────────────────────────
const contactSearch = ref('');
const contactResults = ref([]);
const selectedContact = ref(null);
const isSearchingContact = ref(false);
const contactMode = ref('search');

let searchTimeout = null;
const searchContacts = async query => {
  if (!query || query.length < 2) { contactResults.value = []; return; }
  isSearchingContact.value = true;
  try {
    const res = await contactsAPI.search(query, 1, 'name');
    contactResults.value = (res.data?.payload || []).slice(0, 6);
  } catch { contactResults.value = []; }
  finally { isSearchingContact.value = false; }
};

watch(contactSearch, val => {
  clearTimeout(searchTimeout);
  if (selectedContact.value) return;
  searchTimeout = setTimeout(() => searchContacts(val), 300);
});

const selectContact = contact => {
  selectedContact.value = contact;
  contactSearch.value = contact.name;
  contactResults.value = [];
  form.value.attendee_name = contact.name;
  form.value.attendee_email = contact.email || '';
  form.value.attendee_phone = contact.phone_number || '';
};

const clearContact = () => {
  selectedContact.value = null;
  contactSearch.value = '';
  contactResults.value = [];
};

// ─── Date & slot ─────────────────────────────────────────
const selectedDate = ref(
  props.editingBooking
    ? props.editingBooking.starts_at.slice(0, 10)
    : props.preselectedSlot?.starts_at
      ? props.preselectedSlot.starts_at.slice(0, 10)
      : props.preselectedDate
        ? format(props.preselectedDate, 'yyyy-MM-dd')
        : format(new Date(), 'yyyy-MM-dd')
);

const slotsForSelectedDate = computed(() => {
  if (!selectedDate.value) return [];
  return props.availability.filter(s => {
    try { return s.starts_at.startsWith(selectedDate.value) && s.status === 'available'; }
    catch { return false; }
  });
});

// ─── Form ────────────────────────────────────────────────
const STATUS_OPTIONS = [
  { value: 'confirmed', label: 'Confirmado' },
  { value: 'pending', label: 'Pendente' },
  { value: 'completed', label: 'Concluído' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'no_show', label: 'Não Compareceu - No Show' },
];

const ORIGIN_OPTIONS = computed(() => props.originOptions?.length > 0 ? props.originOptions : ['WhatsApp', 'Instagram', 'Balcão']);

const form = ref({
  attendee_name: '',
  attendee_email: '',
  attendee_phone: '',
  starts_at: props.preselectedSlot?.starts_at || '',
  ends_at: '',
  slot_quantity: 1,
  appointment_resource_id: '',
  notes: '',
  status: 'confirmed',
  products: [],
  appointment_type: '',
  origin: '',
  sale_value: '',
});

const scheduleProducts = computed(() => props.schedule?.integration_settings?.products || []);
const scheduleAppointmentTypes = computed(() => props.schedule?.integration_settings?.appointment_types || []);

// Populate form from editing booking
onMounted(() => {
  if (props.editingBooking) {
    const b = props.editingBooking;
    form.value = {
      attendee_name: b.attendee_name || '',
      attendee_email: b.attendee_email || '',
      attendee_phone: b.attendee_phone || '',
      starts_at: b.starts_at,
      ends_at: b.ends_at || '',
      slot_quantity: b.slot_quantity || 1,
      appointment_resource_id: b.appointment_resource_id || '',
      notes: b.notes || '',
      status: b.status || 'confirmed',
      products: Array.isArray(b.metadata?.product) ? b.metadata.product : (b.metadata?.product ? [b.metadata.product] : []),
      appointment_type: b.metadata?.appointment_type || '',
      origin: b.metadata?.origin || '',
      sale_value: b.sale_value || '',
    };
    if (b.attendee_name) {
      contactSearch.value = b.attendee_name;
      contactMode.value = 'manual';
    }
  }
});

watch(
  () => props.preselectedSlot,
  slot => { if (slot && !isEditMode.value) form.value.starts_at = slot.starts_at; }
);

watch(selectedDate, newDate => {
  if (!isEditMode.value) { form.value.starts_at = ''; return; }
  if (!newDate || !form.value.starts_at) return;
  try {
    const currentTime = format(parseISO(form.value.starts_at), 'HH:mm');
    const d = new Date(`${newDate}T${currentTime}`);
    form.value.starts_at = d.toISOString();
    if (props.schedule?.duration_minutes) {
      form.value.ends_at = new Date(d.getTime() + props.schedule.duration_minutes * 60 * 1000).toISOString();
    }
  } catch { /* ignore */ }
});

const onEditTimeChange = e => {
  const timeVal = e.target.value;
  if (!timeVal || !selectedDate.value) return;
  const d = new Date(`${selectedDate.value}T${timeVal}`);
  form.value.starts_at = d.toISOString();
  if (props.schedule?.duration_minutes) {
    form.value.ends_at = new Date(d.getTime() + props.schedule.duration_minutes * 60 * 1000).toISOString();
  }
};

const isValid = computed(() => {
  if (isBlockMode.value) return form.value.starts_at && form.value.ends_at;
  return form.value.attendee_name.trim() && form.value.starts_at;
});

const handleSubmit = () => {
  if (!isValid.value || props.isSaving) return;

  if (isBlockMode.value) {
    const data = {
      booking_type: 'block',
      starts_at: form.value.starts_at,
      ends_at: form.value.ends_at,
      notes: form.value.notes,
      appointment_resource_id: form.value.appointment_resource_id || undefined,
      appointment_schedule_id: props.schedule?.id,
      status: 'confirmed',
    };
    if (isEditMode.value) {
      emit('update', { id: props.editingBooking.id, ...data });
    } else {
      emit('save', data);
    }
    return;
  }

  const { products, appointment_type, origin, ends_at, sale_value, ...formRest } = form.value;
  const data = {
    ...formRest,
    ...(isEditMode.value && ends_at ? { ends_at } : {}),
    sale_value: sale_value !== '' && sale_value !== null ? parseFloat(sale_value) : undefined,
    contact_id: selectedContact.value?.id || undefined,
    appointment_schedule_id: props.schedule?.id,
    metadata: {
      ...(props.editingBooking?.metadata || {}),
      product: products.length > 0 ? products : null,
      appointment_type: appointment_type || null,
      origin: origin || null,
    },
  };
  if (isEditMode.value) {
    emit('update', { id: props.editingBooking.id, ...data });
  } else {
    emit('save', data);
  }
};
</script>

<template>
  <Teleport to="body">
    <div class="fixed inset-0 z-50 flex items-center justify-center p-4" @click.self="emit('close')">
      <div class="absolute inset-0 bg-black/40 backdrop-blur-sm" @click="emit('close')" />

      <div class="relative bg-n-background rounded-2xl shadow-2xl border border-n-weak w-full max-w-lg max-h-[90vh] overflow-y-auto z-10">

        <!-- Header -->
        <div class="flex items-center justify-between px-6 py-4 border-b border-n-weak">
          <div>
            <h2 class="text-base font-semibold text-n-slate-12">
              <template v-if="isBlockMode">{{ isEditMode ? 'Editar bloqueio' : 'Bloquear horário' }}</template>
              <template v-else>{{ isEditMode ? 'Editar agendamento' : 'Novo agendamento' }}</template>
            </h2>
            <p v-if="schedule" class="text-xs text-n-slate-9 mt-0.5">
              {{ schedule.title }} · {{ schedule.duration_minutes }}min
            </p>
          </div>
          <button
            class="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-n-alpha-2 text-n-slate-9 transition-colors"
            @click="emit('close')"
          >
            <span class="i-lucide-x size-4" />
          </button>
        </div>

        <form class="px-6 py-5 space-y-5" @submit.prevent="handleSubmit">

          <!-- ─── Bloquear horário ─── -->
          <div v-if="isBlockMode" class="space-y-3">
            <div class="flex items-center gap-2 px-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl">
              <span class="i-lucide-ban size-4 text-slate-500" />
              <span class="text-sm font-medium text-slate-700">Este horário ficará indisponível para agendamentos</span>
            </div>

            <div class="grid grid-cols-2 gap-2.5">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Início <span class="text-red-500">*</span></label>
                <input
                  v-model="form.starts_at"
                  type="datetime-local"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Fim <span class="text-red-500">*</span></label>
                <input
                  v-model="form.ends_at"
                  type="datetime-local"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Profissional (opcional)</label>
              <select
                v-model="form.appointment_resource_id"
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
              >
                <option value="">Todos os profissionais</option>
                <option v-for="resource in resources.filter(r => r.active)" :key="resource.id" :value="resource.id">
                  {{ resource.name }}
                </option>
              </select>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Motivo (opcional)</label>
              <input
                v-model="form.notes"
                type="text"
                placeholder="Ex: Almoço, Reunião interna..."
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
              />
            </div>
          </div>

          <!-- ─── Participante ─── -->
          <div v-if="!isBlockMode" class="space-y-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">Participante</span>
              <div class="flex-1 h-px bg-n-weak" />
              <button
                type="button"
                class="text-xs text-n-brand hover:underline"
                @click="contactMode = contactMode === 'search' ? 'manual' : 'search'"
              >
                {{ contactMode === 'search' ? 'Digitar manualmente' : 'Buscar contato' }}
              </button>
            </div>

            <!-- Contact search -->
            <div v-if="contactMode === 'search'" class="relative">
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-8" />
                <input
                  v-model="contactSearch"
                  type="text"
                  placeholder="Buscar contato salvo..."
                  class="w-full pl-8 pr-8 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                  :class="selectedContact ? 'bg-emerald-50 border-emerald-200' : ''"
                />
                <button
                  v-if="selectedContact"
                  type="button"
                  class="absolute right-2.5 top-1/2 -translate-y-1/2 text-n-slate-8 hover:text-n-slate-12"
                  @click="clearContact"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
                <span
                  v-else-if="isSearchingContact"
                  class="absolute right-3 top-1/2 -translate-y-1/2 i-lucide-loader-2 size-3.5 animate-spin text-n-slate-8"
                />
              </div>
              <div
                v-if="selectedContact"
                class="mt-2 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl"
              >
                <span class="i-lucide-user-check size-3.5 text-emerald-600" />
                <div class="flex-1 min-w-0">
                  <div class="text-xs font-semibold text-emerald-800 truncate">{{ selectedContact.name }}</div>
                  <div class="text-xs text-emerald-600 truncate">{{ selectedContact.email || selectedContact.phone_number }}</div>
                </div>
                <span class="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">Contato Chatwoot</span>
              </div>
              <div
                v-if="contactResults.length > 0 && !selectedContact"
                class="absolute z-10 mt-1 w-full bg-n-background border border-n-weak rounded-xl shadow-xl overflow-hidden"
              >
                <button
                  v-for="contact in contactResults"
                  :key="contact.id"
                  type="button"
                  class="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-n-alpha-1 transition-colors text-left"
                  @click="selectContact(contact)"
                >
                  <div class="flex-shrink-0 w-7 h-7 rounded-full bg-n-alpha-2 flex items-center justify-center text-xs font-semibold text-n-slate-11">
                    {{ contact.name[0]?.toUpperCase() }}
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-n-slate-12 truncate">{{ contact.name }}</div>
                    <div class="text-xs text-n-slate-9 truncate">{{ contact.email || contact.phone_number || '—' }}</div>
                  </div>
                </button>
              </div>
            </div>

            <!-- Manual banner -->
            <div
              v-if="contactMode === 'manual'"
              class="flex items-center gap-2 px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-xl"
            >
              <span class="i-lucide-user-plus size-3.5 text-n-slate-9" />
              <span class="text-xs text-n-slate-9">Novo contato — não será salvo no Chatwoot</span>
            </div>

            <div class="space-y-2.5">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Nome completo <span class="text-red-500">*</span></label>
                <input
                  v-model="form.attendee_name"
                  type="text"
                  placeholder="João Silva"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
              </div>
              <div class="grid grid-cols-2 gap-2.5">
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">E-mail</label>
                  <input v-model="form.attendee_email" type="email" placeholder="joao@email.com" class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all" />
                </div>
                <div>
                  <label class="block text-xs font-medium text-n-slate-11 mb-1">Telefone</label>
                  <input v-model="form.attendee_phone" type="tel" placeholder="+55 11 99999-0000" class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all" />
                </div>
              </div>
            </div>
          </div>

          <!-- ─── Horário ─── -->
          <div v-if="!isBlockMode" class="space-y-3">
            <div class="flex items-center gap-2">
              <span class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">Horário</span>
              <div class="flex-1 h-px bg-n-weak" />
            </div>

            <div class="grid grid-cols-2 gap-2.5">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Data</label>
                <input
                  v-model="selectedDate"
                  type="date"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">
                  Slot <span v-if="!isEditMode" class="text-red-500">*</span>
                </label>
                <!-- In edit mode show an editable time field -->
                <input
                  v-if="isEditMode"
                  type="time"
                  :value="form.starts_at ? format(parseISO(form.starts_at), 'HH:mm') : ''"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                  @change="onEditTimeChange"
                />
                <select
                  v-else
                  v-model="form.starts_at"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                >
                  <option value="">Selecione...</option>
                  <option v-for="slot in slotsForSelectedDate" :key="slot.starts_at" :value="slot.starts_at">
                    {{ format(parseISO(slot.starts_at), 'HH:mm') }}
                    ({{ slot.remaining_slots }} vaga{{ slot.remaining_slots !== 1 ? 's' : '' }})
                  </option>
                </select>
                <p v-if="!isEditMode && selectedDate && slotsForSelectedDate.length === 0" class="text-xs text-amber-600 mt-1">
                  Nenhum slot disponível para esta data.
                </p>
              </div>
            </div>

            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Profissional responsável</label>
              <select
                v-model="form.appointment_resource_id"
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
              >
                <option value="">Sem profissional específico</option>
                <option v-for="resource in resources.filter(r => r.active)" :key="resource.id" :value="resource.id">
                  {{ resource.name }}
                </option>
              </select>
            </div>

            <!-- Product (multi-select) -->
            <div v-if="scheduleProducts.length > 0">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Produto / Serviço
                <span v-if="form.products.length > 0" class="ml-1 text-n-brand font-normal">({{ form.products.length }} selecionado{{ form.products.length > 1 ? 's' : '' }})</span>
              </label>
              <div class="rounded-xl border border-n-weak bg-n-background divide-y divide-n-weak overflow-hidden">
                <label
                  v-for="p in scheduleProducts"
                  :key="p"
                  class="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
                >
                  <input
                    type="checkbox"
                    :value="p"
                    v-model="form.products"
                    class="w-3.5 h-3.5 rounded accent-n-brand cursor-pointer"
                  />
                  <span class="text-sm text-n-slate-12">{{ p }}</span>
                </label>
              </div>
            </div>

            <!-- Appointment type -->
            <div v-if="scheduleAppointmentTypes.length > 0">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Tipo de agendamento</label>
              <select
                v-model="form.appointment_type"
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
              >
                <option value="">Sem tipo específico</option>
                <option v-for="t in scheduleAppointmentTypes" :key="t" :value="t">{{ t }}</option>
              </select>
            </div>

            <!-- Origin -->
            <div>
              <label class="block text-xs font-medium text-n-slate-11 mb-1">Origem</label>
              <select
                v-model="form.origin"
                class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
              >
                <option value="">Não informado</option>
                <option v-for="o in ORIGIN_OPTIONS" :key="o" :value="o">{{ o }}</option>
              </select>
            </div>

            <div class="grid grid-cols-2 gap-2.5">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Vagas ocupadas</label>
                <input
                  v-model.number="form.slot_quantity"
                  type="number"
                  min="1"
                  :max="schedule?.slot_capacity || 10"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1">Status</label>
                <select
                  v-model="form.status"
                  class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
                >
                  <option v-for="opt in STATUS_OPTIONS" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                </select>
              </div>
            </div>
          </div>

          <!-- ─── Observações ─── -->
          <div v-if="!isBlockMode">
            <label class="block text-xs font-medium text-n-slate-11 mb-1">Observações</label>
            <textarea
              v-model="form.notes"
              rows="2"
              placeholder="Informações adicionais..."
              class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all resize-none"
            />
          </div>

          <!-- ─── Valor da venda ─── -->
          <div v-if="!isBlockMode">
            <label class="block text-xs font-medium text-n-slate-11 mb-1">
              Valor da venda (R$)
              <span class="text-xs text-n-slate-9 font-normal ml-1">— registra conversão ao concluir</span>
            </label>
            <input
              v-model.number="form.sale_value"
              type="number"
              step="0.01"
              min="0"
              placeholder="0,00"
              class="w-full px-3 py-2.5 text-sm rounded-xl border border-n-weak bg-n-background text-n-slate-12 placeholder-n-slate-8 focus:outline-none focus:ring-2 focus:ring-n-brand/30 focus:border-n-brand transition-all"
            />
          </div>

          <!-- ─── Footer ─── -->
          <div class="flex items-center justify-end gap-2 pt-2 border-t border-n-weak">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2 rounded-xl transition-colors"
              @click="emit('close')"
            >
              Cancelar
            </button>
            <button
              type="submit"
              :disabled="!isValid || isSaving"
              class="flex items-center gap-2 px-5 py-2 text-sm font-medium bg-n-brand text-white rounded-xl hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              <span v-if="isSaving" class="i-lucide-loader-2 size-3.5 animate-spin" />
              <span v-else :class="isBlockMode ? 'i-lucide-ban' : isEditMode ? 'i-lucide-save' : 'i-lucide-calendar-check'" class="size-3.5" />
              {{ isSaving ? 'Salvando...' : isBlockMode ? (isEditMode ? 'Salvar bloqueio' : 'Bloquear horário') : isEditMode ? 'Salvar alterações' : 'Registrar agendamento' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </Teleport>
</template>
